import { useQuery, useMutation, keepPreviousData } from "@tanstack/react-query";
import { queryClient } from "@/shared/services/queryClient";
import {
  accountingApi,
  type JournalEntryListParams,
  type CreateAccountDto,
  type UpdateAccountDto,
  type CreateJournalEntryDto,
  type RejectJournalEntryDto,
  type ReverseJournalEntryDto,
  type CreateCostCentreDto,
  type UpdateCostCentreDto,
  type CreateCompanyDto,
  type UpdateCompanyDto,
  type CreateBankAccountDto,
  type UpdateBankAccountDto,
  type CreateBankTransactionDto,
  type CreateBudgetDto,
  type UpdateBudgetLineDto,
} from "../services/accountingApi";

// ============================================================================
// Chart of Accounts
// ============================================================================

export const useAccountTree = () =>
  useQuery({ queryKey: ["accounts", "tree"], queryFn: accountingApi.getAccountTree });

export const useAccounts = () =>
  useQuery({ queryKey: ["accounts"], queryFn: accountingApi.getAccounts });

export const usePostableAccounts = () =>
  useQuery({ queryKey: ["accounts", "postable"], queryFn: accountingApi.getPostableAccounts });

export const useAccountById = (id: string) =>
  useQuery({ queryKey: ["accounts", id], queryFn: () => accountingApi.getAccountById(id), enabled: !!id });

export const useCreateAccount = () =>
  useMutation({
    mutationFn: (dto: CreateAccountDto) => accountingApi.createAccount(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["accounts"] });
    },
  });

export const useUpdateAccount = () =>
  useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: UpdateAccountDto }) => accountingApi.updateAccount(id, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["accounts"] });
    },
  });

export const useDeactivateAccount = () =>
  useMutation({
    mutationFn: (id: string) => accountingApi.deactivateAccount(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["accounts"] });
    },
  });

// ============================================================================
// Journal Entries
// ============================================================================

export const useJournalUserOptions = (enabled = true) =>
  useQuery({
    queryKey: ["journal-entries", "user-options"],
    queryFn: accountingApi.getJournalUserOptions,
    enabled,
    staleTime: 5 * 60 * 1000,
  });

export const useJournalEntriesPaged = (params: JournalEntryListParams) =>
  useQuery({
    queryKey: [
      "journal-entries",
      params.pageNumber,
      params.pageSize,
      params.search ?? "",
      params.status ?? null,
      params.startDate ?? null,
      params.endDate ?? null,
      params.createdByUserId ?? null,
    ],
    queryFn: () => accountingApi.getJournalEntriesPaged(params),
    placeholderData: keepPreviousData,
  });

export const useJournalEntryById = (id?: string) =>
  useQuery({ queryKey: ["journal-entries", id], queryFn: () => accountingApi.getJournalEntryById(id!), enabled: !!id });

export const useJournalEntriesByStatus = (status: number) =>
  useQuery({ queryKey: ["journal-entries", "status", status], queryFn: () => accountingApi.getJournalEntriesByStatus(status), enabled: status >= 0 });

export const useCreateJournalEntry = () =>
  useMutation({
    mutationFn: (dto: CreateJournalEntryDto) => accountingApi.createJournalEntry(dto),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["journal-entries"] }); },
  });

export const useUpdateJournalEntry = () =>
  useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: CreateJournalEntryDto }) => accountingApi.updateJournalEntry(id, dto),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["journal-entries"] }); },
  });

export const useSubmitJournalEntry = () =>
  useMutation({
    mutationFn: (id: string) => accountingApi.submitJournalEntry(id),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["journal-entries"] }); },
  });

export const useApproveJournalEntry = () =>
  useMutation({
    mutationFn: (id: string) => accountingApi.approveJournalEntry(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["journal-entries"] });
      queryClient.invalidateQueries({ queryKey: ["sales-invoices"] });
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      queryClient.invalidateQueries({ queryKey: ["credit-notes"] });
    },
  });

export const useRejectJournalEntry = () =>
  useMutation({
    mutationFn: (dto: RejectJournalEntryDto) => accountingApi.rejectJournalEntry(dto),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["journal-entries"] }); },
  });

export const useReverseJournalEntry = () =>
  useMutation({
    mutationFn: (dto: ReverseJournalEntryDto) => accountingApi.reverseJournalEntry(dto),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["journal-entries"] }); },
  });

// ============================================================================
// Companies
// ============================================================================

export const useCompanies = () =>
  useQuery({ queryKey: ["companies"], queryFn: accountingApi.getCompanies });

export const useCompanySummaries = () =>
  useQuery({ queryKey: ["companies", "summaries"], queryFn: accountingApi.getCompanySummaries });

export const useCreateCompany = () =>
  useMutation({
    mutationFn: (dto: CreateCompanyDto) => accountingApi.createCompany(dto),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["companies"] }); },
  });

export const useUpdateCompany = () =>
  useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: UpdateCompanyDto }) => accountingApi.updateCompany(id, dto),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["companies"] }); },
  });

export const useDeactivateCompany = () =>
  useMutation({
    mutationFn: (id: string) => accountingApi.deactivateCompany(id),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["companies"] }); },
  });

// ============================================================================
// Cost Centres
// ============================================================================

export const useCostCentres = () =>
  useQuery({ queryKey: ["cost-centres"], queryFn: accountingApi.getCostCentres });

export const useCostCentreTree = () =>
  useQuery({ queryKey: ["cost-centres", "tree"], queryFn: accountingApi.getCostCentreTree });

export const useCreateCostCentre = () =>
  useMutation({
    mutationFn: (dto: CreateCostCentreDto) => accountingApi.createCostCentre(dto),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["cost-centres"] }); },
  });

export const useUpdateCostCentre = () =>
  useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: UpdateCostCentreDto }) => accountingApi.updateCostCentre(id, dto),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["cost-centres"] }); },
  });

export const useDeactivateCostCentre = () =>
  useMutation({
    mutationFn: (id: string) => accountingApi.deactivateCostCentre(id),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["cost-centres"] }); },
  });

// ============================================================================
// Bank Accounts
// ============================================================================

export const useBankAccounts = () =>
  useQuery({ queryKey: ["bank-accounts"], queryFn: accountingApi.getBankAccounts });

export const useBankAccountById = (id?: string) =>
  useQuery({ queryKey: ["bank-accounts", id], queryFn: () => accountingApi.getBankAccountById(id!), enabled: !!id });

export const useCreateBankAccount = () =>
  useMutation({
    mutationFn: (dto: CreateBankAccountDto) => accountingApi.createBankAccount(dto),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["bank-accounts"] }); },
  });

export const useUpdateBankAccount = () =>
  useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: UpdateBankAccountDto }) => accountingApi.updateBankAccount(id, dto),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["bank-accounts"] }); },
  });

export const useBankTransactions = (bankAccountId?: string, startDate?: string, endDate?: string) =>
  useQuery({
    queryKey: ["bank-transactions", bankAccountId, startDate, endDate],
    queryFn: () => accountingApi.getBankTransactions(bankAccountId!, startDate, endDate),
    enabled: !!bankAccountId,
  });

export const useCreateBankTransaction = () =>
  useMutation({
    mutationFn: ({ bankAccountId, dto }: { bankAccountId: string; dto: CreateBankTransactionDto }) =>
      accountingApi.createBankTransaction(bankAccountId, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bank-transactions"] });
      queryClient.invalidateQueries({ queryKey: ["bank-accounts"] });
    },
  });

export const useReconcileTransaction = () =>
  useMutation({
    mutationFn: (dto: { bankTransactionId: string; journalEntryId: string }) => accountingApi.reconcileTransaction(dto),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["bank-transactions"] }); },
  });

// ============================================================================
// Budgets
// ============================================================================

export const useBudgets = () =>
  useQuery({ queryKey: ["budgets"], queryFn: accountingApi.getBudgets });

export const useBudgetById = (id?: string) =>
  useQuery({ queryKey: ["budgets", id], queryFn: () => accountingApi.getBudgetById(id!), enabled: !!id });

export const useCreateBudget = () =>
  useMutation({
    mutationFn: (dto: CreateBudgetDto) => accountingApi.createBudget(dto),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["budgets"] }); },
  });

export const useUpdateBudgetLine = () =>
  useMutation({
    mutationFn: (dto: UpdateBudgetLineDto) => accountingApi.updateBudgetLine(dto),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["budgets"] }); },
  });

export const useApproveBudget = () =>
  useMutation({
    mutationFn: (id: string) => accountingApi.approveBudget(id),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["budgets"] }); },
  });

export const useBudgetVarianceReport = (id?: string) =>
  useQuery({ queryKey: ["budgets", id, "variance"], queryFn: () => accountingApi.getBudgetVarianceReport(id!), enabled: !!id });
